import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Typography,
    Paper,
    Snackbar,
    Alert,
    MenuItem,
    Select,
    Alert as MuiAlert,
    FormControl,
    InputLabel,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { createCmsContent, updateCmsContent, getCmsContentByType } from "../../Apis/CmsContentApi";

const allowedTypes = ["Terms and Conditions", "Privacy Policy"];

const CreateCmsContent = () => {
    const { type } = useParams();
    const navigate = useNavigate();

    const [contentType, setContentType] = useState(type || "");
    const [descriptions, setDescriptions] = useState("");
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const editMode = Boolean(type);

    useEffect(() => {
        if (editMode) {
            getCmsContentByType(type)
                .then((res) => {
                    const data = res?.data || res;
                    setContentType(data.contentType || "");
                    setDescriptions(data.descriptions || "");
                })
                .catch(() => setErrorMsg("Failed to load CMS content."));
        }
    }, [editMode, type]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!contentType || !descriptions) {
            setErrorMsg("Please fill all fields.");
            return;
        }
        const formData = { contentType, descriptions };
        try {
            setLoading(true);
            if (editMode) {
                await updateCmsContent(formData);
            } else {
                await createCmsContent(formData);
            }
            setSuccess(true);
            setTimeout(() => navigate("/cmscontentlist"), 1200);
        } catch {
            setErrorMsg(editMode ? "Failed to update content." : "Privacy Policy already exists. Use update instead.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", px: 2 }}>
            <Paper sx={{ width: { xs: "100%", sm: "80%", md: "40%" }, p: 4, borderRadius: 3, boxShadow: 6 }}>
                <Typography variant="h5" gutterBottom textAlign="center">
                    {editMode ? "Update CMS Content" : "Create CMS Content"}
                </Typography>

                <form onSubmit={handleSubmit}>
                    <FormControl fullWidth margin="normal">
                        <InputLabel id="content-type-label">Content Type</InputLabel>
                        <Select
                            labelId="content-type-label"
                            value={contentType}
                            onChange={(e) => setContentType(e.target.value)}
                            label="Content Type"
                            disabled={editMode} // Can't change type in edit mode
                            sx={{ height: 40 }}
                        >
                            {allowedTypes.map((typeOption) => (
                                <MenuItem key={typeOption} value={typeOption}>
                                    {typeOption}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>


                    <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
                        Descriptions
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                        <ReactQuill
                            theme="snow"
                            value={descriptions}
                            onChange={setDescriptions}
                            style={{ height: 200 }}
                        />
                    </Box>

                    {/* Space added above the button */}
                    <Box sx={{ mt: 3 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={loading}
                            sx={{ marginTop: 5, height: 40 }}
                        >
                            {loading ? (editMode ? "Updating..." : "Creating...") : editMode ? "Update Content" : "Create Content"}
                        </Button>
                    </Box>
                </form>
            </Paper>

            {/* Success Snackbar */}
            <Snackbar
                open={success}
                autoHideDuration={3000}
                onClose={() => setSuccess(false)}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <MuiAlert onClose={() => setSuccess(false)} severity="success" variant="filled" sx={{ width: "100%" }}>
                    {editMode ? "Content updated successfully!" : "Content created successfully!"}
                </MuiAlert>
            </Snackbar>

            {/* Error Snackbar */}
            <Snackbar
                open={!!errorMsg}
                autoHideDuration={4000}
                onClose={() => setErrorMsg("")}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <MuiAlert onClose={() => setErrorMsg("")} severity="error" variant="filled" sx={{ width: "100%" }}>
                    {errorMsg}
                </MuiAlert>
            </Snackbar>
        </Box>

    );
};

export default CreateCmsContent;
